import { useMemo, useReducer } from "react";
import { type BenchRow, newBenchRow } from "./bench";
import { isPrefixOf } from "./quoteRow";
import type { SubmitResult } from "./types";

export const STEPS = [
  "Contratos actuales",
  "Ahorro",
  "Selección",
  "Resumen",
  "Cierre",
] as const;

// The slider moves in tens: the selection is always the first N% of the list.
export const PCT_STEP = 10;
const SELECTION_STEP = 2;
// Resumen: going back before it means the selection may change.
const SUMMARY_STEP = 3;
const CLOSING_STEP = 4;

// Why the merchant was sent back from Resumen.
export type Notice =
  | "quoteChanged"
  | "screeningsUnavailable"
  // A new screening sorted above the selection.
  | "listChanged";

// The slider position closest to `count` of `total`.
const nearestPct = (count: number, total: number): number =>
  total > 0 ? Math.round(((count / total) * 100) / PCT_STEP) * PCT_STEP : 0;

type State = {
  step: number;
  bench: BenchRow[];
  selected: ReadonlySet<string>;
  // Slider position, 0-100 in steps of PCT_STEP.
  pct: number;
  search: string;
  provider: string | undefined;
  withVat: boolean;
  accuracy: boolean;
  notice: Notice | null;
  // What `submit` returned; set means the batch exists.
  result: SubmitResult | null;
};

type Action =
  | { type: "addBenchRow" }
  | { type: "removeBenchRow"; id: number }
  | { type: "editBenchRow"; id: number; patch: Partial<Omit<BenchRow, "id">> }
  | { type: "setPct"; pct: number; orderedIds: string[] }
  | { type: "setSearch"; search: string }
  | { type: "setProvider"; provider: string | undefined }
  | { type: "setWithVat"; withVat: boolean }
  | { type: "setAccuracy"; accepted: boolean }
  | { type: "rejected"; failure: Notice; orderedIds?: string[] }
  | { type: "submitted"; result: SubmitResult }
  | { type: "goTo"; step: number };

const initialState = (): State => ({
  step: 0,
  bench: [newBenchRow()],
  selected: new Set(),
  pct: 0,
  search: "",
  provider: undefined,
  withVat: true,
  accuracy: false,
  notice: null,
  result: null,
});

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "addBenchRow":
      return { ...state, bench: [...state.bench, newBenchRow()] };
    case "removeBenchRow":
      return {
        ...state,
        bench: state.bench.filter((row) => row.id !== action.id),
      };
    case "editBenchRow":
      return {
        ...state,
        bench: state.bench.map((row) =>
          row.id === action.id ? { ...row, ...action.patch } : row
        ),
      };
    case "setPct": {
      // The server orders the list; the slider takes the first N percent of it.
      const count = Math.round((action.orderedIds.length * action.pct) / 100);
      return {
        ...state,
        pct: action.pct,
        selected: new Set(action.orderedIds.slice(0, count)),
      };
    }
    case "setSearch":
      return { ...state, search: action.search };
    case "setProvider":
      return { ...state, provider: action.provider };
    case "setWithVat":
      return { ...state, withVat: action.withVat };
    case "setAccuracy":
      return { ...state, accuracy: action.accepted };
    case "rejected": {
      const { orderedIds } = action;
      // No ids come with the error: keep what is still on the fresh list.
      const available = orderedIds && new Set(orderedIds);
      const kept = available
        ? new Set([...state.selected].filter((id) => available.has(id)))
        : state.selected;
      // Rows that went away (or none left) mean the merchant has to choose
      // again, whatever the code said.
      const lostRows = kept.size < state.selected.size || !kept.size;
      // A screening that sorts above the selection breaks it as "the first N":
      // it has no row to confirm. Keep the same count over the fresh order.
      const reordered = !!orderedIds && !isPrefixOf(orderedIds, kept);
      const selected =
        orderedIds && reordered
          ? new Set(orderedIds.slice(0, kept.size))
          : kept;
      const notice: Notice =
        action.failure === "screeningsUnavailable" || lostRows
          ? "screeningsUnavailable"
          : reordered
          ? "listChanged"
          : "quoteChanged";

      return {
        ...state,
        selected,
        pct:
          orderedIds && (lostRows || reordered)
            ? nearestPct(selected.size, orderedIds.length)
            : state.pct,
        notice,
        step: notice === "quoteChanged" ? SUMMARY_STEP : SELECTION_STEP,
        accuracy: false,
      };
    }
    case "submitted":
      return { ...state, result: action.result, step: CLOSING_STEP };
    case "goTo": {
      // Back before Resumen, the selection may change: declare again.
      const reopened = action.step < SUMMARY_STEP;
      return {
        ...state,
        step: action.step,
        notice: null,
        accuracy: reopened ? false : state.accuracy,
      };
    }
  }
};

export function useInductionWizard() {
  const [state, dispatch] = useReducer(reducer, undefined, initialState);

  const actions = useMemo(
    () => ({
      addBenchRow: () => dispatch({ type: "addBenchRow" }),
      removeBenchRow: (id: number) => dispatch({ type: "removeBenchRow", id }),
      editBenchRow: (id: number, patch: Partial<Omit<BenchRow, "id">>) =>
        dispatch({ type: "editBenchRow", id, patch }),
      setPct: (pct: number, orderedIds: string[]) =>
        dispatch({ type: "setPct", pct, orderedIds }),
      setSearch: (search: string) => dispatch({ type: "setSearch", search }),
      setProvider: (provider: string | undefined) =>
        dispatch({ type: "setProvider", provider }),
      setWithVat: (withVat: boolean) =>
        dispatch({ type: "setWithVat", withVat }),
      setAccuracy: (accepted: boolean) =>
        dispatch({ type: "setAccuracy", accepted }),
      rejected: (failure: Notice, orderedIds?: string[]) =>
        dispatch({ type: "rejected", failure, orderedIds }),
      submitted: (result: SubmitResult) =>
        dispatch({ type: "submitted", result }),
      goTo: (step: number) => dispatch({ type: "goTo", step }),
    }),
    []
  );

  return { state, actions };
}
